import { useState } from 'react';
import { AlertTriangle, Check, Copy, Database } from 'lucide-react';

import type { ExecutedQuery } from '../../services/api/dataQa';
import { KvalitetSessionLink } from '../sessions/KvalitetSessionLink';

import {
  formatCell,
  formatDuration,
  isSessionIdCell,
  MAX_VISIBLE_ROWS,
  toCsv,
} from './result-format';

const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const ResultTable = ({ query }: { query: ExecutedQuery }) => {
  const [copied, setCopied] = useState(false);
  const visible = query.rows.slice(0, MAX_VISIBLE_ROWS);
  const total = query.rowCount ?? query.rows.length;

  const handleCopy = async () => {
    const ok = await copyText(toCsv(query.columns, query.rows));
    setCopied(ok);
    if (ok) window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-3">
      <div className="mb-2 flex items-center justify-between gap-3 text-xs text-text-grey">
        <span>
          {visible.length < total
            ? `Visar ${visible.length} av ${total} rader`
            : `${total} ${total === 1 ? 'rad' : 'rader'}`}
          {query.truncated ? ' (kapat vid 500)' : ''}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1 rounded-sm border border-black/10 bg-white px-2 py-1 text-xs text-brand-blue transition-colors hover:border-brand-blue"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? 'Kopierat' : 'Kopiera som CSV'}
        </button>
      </div>
      <div className="overflow-x-auto rounded-sm border border-black/10 bg-white">
        <table className="min-w-full text-left text-xs">
          <thead className="bg-brand-beige-tint-2 text-brand-blue">
            <tr>
              {query.columns.map((column) => (
                <th key={column} className="whitespace-nowrap px-3 py-2 font-semibold">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, index) => (
              <tr key={index} className="border-t border-black/5">
                {query.columns.map((column) => {
                  const value = row[column];
                  return (
                    <td key={column} className="whitespace-nowrap px-3 py-1.5 text-black/80">
                      {isSessionIdCell(column, value) ? (
                        <KvalitetSessionLink sessionId={value} newTab />
                      ) : (
                        formatCell(value)
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const QueryDetails = ({ query, index }: { query: ExecutedQuery; index: number }) => {
  const [open, setOpen] = useState(false);
  const failed = query.error !== null;

  return (
    <div className="min-w-0 rounded-sm border border-black/10 bg-brand-beige-tint-2/40">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-brand-blue hover:bg-brand-beige-tint-2"
      >
        {failed ? (
          <AlertTriangle className="h-3.5 w-3.5 text-brand-sale-red" />
        ) : (
          <Database className="h-3.5 w-3.5" />
        )}
        <span className="whitespace-nowrap font-semibold">
          {open ? 'Dölj hur jag räknade' : 'Visa hur jag räknade'}
        </span>
        <span className="truncate text-text-grey">
          {index + 1}. {query.purpose}
        </span>
        <span className="ml-auto whitespace-nowrap text-text-grey">
          {failed ? 'fel' : `${query.rowCount ?? 0} ${query.rowCount === 1 ? 'rad' : 'rader'}`} ·{' '}
          {formatDuration(query.durationMs)}
        </span>
      </button>
      {open ? (
        <div className="border-t border-black/10 px-3 py-3">
          <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-sm bg-brand-blue px-3 py-2 font-mono text-xs text-white">
            {query.sql}
          </pre>
          {failed ? (
            <p className="mt-2 text-xs text-brand-sale-red">{query.error}</p>
          ) : query.rows.length > 0 ? (
            <ResultTable query={query} />
          ) : (
            <p className="mt-2 text-xs text-text-grey">
              {query.rowCount === 0
                ? 'Frågan gav inga rader.'
                : 'Raderna visas för den sista frågan i svaret.'}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
};
